//@flow
import * as React from 'react';
import { deriveSlotType } from 'common/entity/components/dayShift/WorkingDaysSelectorModal';
import slotsSummaryFactory from 'common/entity/components/slot/SlotsSummaryFactory';
import { DivWithPopover } from 'common/components/core/wrappers/Div';
import { PopoverTriggerEnum } from 'common/components/core/popover/popOverFactory';
import theme from './WorkingDaysSummary.scss';
import type { DayShift } from 'common/models/internal/DayShift';
import type { TFunction } from 'react-i18next';
import Icon from 'common/components/core/icon';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import { type ThemeType } from 'react-css-themr';
import DateTimeUtils, {
    DateInputFormats,
    TimeStyle
} from 'common/utils/DateTimeUtils';

type Props = {
    theme: ThemeType,
    data: DayShift,
    t: TFunction,
    isBreaksWithOffset: boolean
};

const daysList = Object.values(DayOfWeekType);

class SummaryComponent extends React.PureComponent<Props> {
    getSlotText = () => {
        const { data: workingday, t, isBreaksWithOffset } = this.props;
        const { slot, dayOfWeek, endDayOffset = 0 } = workingday;
        const start = DateTimeUtils.format(
            slot.start,
            {
                timeStyle: TimeStyle.SHORT
            },
            DateInputFormats.HOUR24_SHORT
        );
        const end = DateTimeUtils.format(
            slot.end,
            {
                timeStyle: TimeStyle.SHORT
            },
            DateInputFormats.HOUR24_SHORT
        );
        if (!isBreaksWithOffset) {
            return t('slotStartToEnd', {
                start,
                end
            });
        }
        const endDay =
            daysList[(daysList.indexOf(dayOfWeek) + endDayOffset + 7) % 7];
        const translatedStartDay = t(
            `entityManagement:shiftTable.days.${dayOfWeek}`
        );
        const translatedEndDay = t(
            `entityManagement:shiftTable.days.${endDay}`
        );
        return (
            <>
                <span className={theme.bold}>
                    {translatedStartDay}
                    ,&nbsp;
                </span>
                <span>
                    {start}
                    &nbsp;
                </span>
                <span>-&nbsp;</span>
                <span className={theme.bold}>
                    {translatedEndDay}
                    ,&nbsp;
                </span>
                <span>{end}</span>
            </>
        );
    };

    render() {
        const { data: workingday, theme, t, isBreaksWithOffset } = this.props;
        const {
            slot,
            breaks,
            startLocation,
            endLocation,
            dayOfWeek
        } = workingday;
        const start = DateTimeUtils.format(
            slot.start,
            {
                timeStyle: TimeStyle.SHORT
            },
            DateInputFormats.HOUR24_SHORT
        );
        const end = DateTimeUtils.format(
            slot.end,
            {
                timeStyle: TimeStyle.SHORT
            },
            DateInputFormats.HOUR24_SHORT
        );
        const noOfBreaks = breaks && breaks.size;
        const startLocationAddress = startLocation?.getAddressString() || null;
        const endLocationAddress = endLocation?.getAddressString() || null;

        const breaksPopover =
            breaks && breaks.size ? (
                <div>
                    {breaks.map((mBreak, index) => {
                        return (
                            <div key={index}>
                                {isBreaksWithOffset
                                    ? mBreak.getBreakWithOffsetDescription(
                                          dayOfWeek
                                      )
                                    : mBreak.getBreakDescription()}
                            </div>
                        );
                    })}
                </div>
            ) : null;
        return (
            <div className={theme.slotInfo}>
                <div className={theme.slots}>
                    {start || end ? this.getSlotText() : '-'}
                    <DivWithPopover
                        className={theme.breaksCount}
                        popOverContent={breaksPopover}
                        triggerPopoverOn={PopoverTriggerEnum.Hover}
                    >
                        {(start || end) && breaks
                            ? t('configure:breakCount_interval', {
                                  postProcess: 'interval',
                                  count: noOfBreaks
                              })
                            : null}
                    </DivWithPopover>
                </div>
                {startLocationAddress || endLocationAddress ? (
                    <div className={theme.locationSummary}>
                        <div className={theme.addressWrap}>
                            <Icon icon={'circle'} theme={theme} />
                            <div className={theme.address}>
                                {startLocationAddress}
                            </div>
                            <div className={theme.connectorLine} />
                        </div>

                        <div className={theme.addressWrap}>
                            <Icon icon={'square'} theme={theme} />
                            <div className={theme.address}>
                                {endLocationAddress}
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }
}

const WorkingDaysSummaryComponent = slotsSummaryFactory({ SummaryComponent });
const WorkingDaysSummary = props => {
    return (
        <WorkingDaysSummaryComponent
            {...props}
            theme={theme}
            deriveSlotType={deriveSlotType}
        />
    );
};
export { WorkingDaysSummary };
export default WorkingDaysSummary;
